<template>
  <div class="form-group">

    <SelectGroup :options="selectModels.source_id"
                 :nullOption="true"
                 :showlabel="true"
                 :translate="false"
                 :required="false"
                 :label="$t('source')"
                 variant="white"
                 v-if="object.notification_type === 'crm_application_created'"
                 v-model="object.source_id"/>

    <div v-if="['before_visit', 'after_visit', 'since_last_visit'].includes(object.notification_type)">
      <SelectGroup :options="notificationVisitTypes"
                   :nullOption="false"
                   :showlabel="true"
                   :required="false"
                   :label="$t('visit_type')"
                   variant="white"
                   v-model="object.visit_type"/>
    </div>

    <div v-if="['after_visit'].includes(object.notification_type)">
      <SelectGroup :options="selectModels.visit_result_id"
                   :nullOption="true"
                   :showlabel="true"
                   :required="false"
                   title="translatedTitle"
                   :label="$t('visit_result')"
                   variant="white"
                   v-model="object.visit_result_id"/>
    </div>

    <TagsList type="notifications"
              class="mt-0">
      {{ $t('tags_for_notifications') }}
    </TagsList>

    <div v-if="rights.includes('confirm_visits_through_message') && eligibleForPatientResponse"
         class="mt-4">
      <CheckboxGroup class="d-inline-block"
                     fid="confirm_visits_through_message"
                     v-model="confirm_visits_through_message"
                     :label="$t('ask_for_confirmation_response_from_patient')"/>
      <div v-if="confirm_visits_through_message">
        <div class="row">
          <div class="col-md-6">
            <InputGroup type="text"
                        :label="$t('confirmation_response')"
                        v-model="object.confirm_response"/>
          </div>
          <div class="col-md-6">
            <InputGroup type="text"
                        :label="$t('cancellation_response')"
                        v-model="object.cancel_response"/>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-4">
      <label class="form-label">{{ $t('notification_channel') }}</label>
      <div>
        <CheckboxGroup v-for="n in notificationChannelsFiltered"
                       class="d-inline-block mr-4"
                       :fid="`tag-${n.id}`"
                       :key="`ch-${n.id}`"
                       :checkboxvalue="n.id"
                       v-model="object.notification_channels"
                       :label="titleForChannel(n)"/>
      </div>
    </div>

    <div v-if="['before_visit', 'after_visit', 'since_last_visit', 'after_service'].includes(object.notification_type)">
      <label class="form-label">{{ $t('send_time') }}</label>
      <div class="row">
        <div class="col-lg-3 col-md-4 col-6">
          <InputGroup class="mb-0"
                      type="number"
                      placeholder="0"
                      v-model="object.minutes"/>
        </div>
        <div class="col-lg-3 col-md-4 col-6">
          <SelectGroup v-model="object.timeType"
                       class="mb-0"
                       :options="timeTypes"
                       variant="white"/>
        </div>
      </div>
      <div class="small text-muted">
        <div v-if="object.notification_type == 'before_visit'">{{ $t('time_before_visit') }}</div>
        <div v-if="object.notification_type == 'after_visit'">{{ $t('time_after_visit') }}</div>
      </div>
    </div>

    <div v-if="['laboratory_order_status_changed'].includes(object.notification_type)" class="row">
      <div class="col-md-12">
        <SelectGroup :options="laboratoryOrderStatuses"
                     :nullOption="true"
                     :showlabel="true"
                     :required="false"
                     :nullOptionTitle="$t('all_statuses')"
                     :label="$t('laboratory_order_status')"
                     variant="white"
                     v-model="object.laboratory_order_status"/>
      </div>
    </div>

    <div v-if="['before_visit_day_in_advance', 'after_visit_day_passed'].includes(object.notification_type)" class="row">
      <div class="col-md-auto">
        <TimeGroup v-model="object.cron_time"
                   :showlabel="true"
                   :label="$t('send_time')"
                   :required="required"/>
      </div>
      <div class="col-md" v-if="['after_visit_day_passed'].includes(object.notification_type)">
        <SelectGroup :options="dayOptionsForAfterTheVisit"
                     :label="$t('day')"
                     :showlabel="true"
                     :translate="true"
                     :null-option="false"
                     variant="white"
                     v-model="object.cron_offset_minutes"/>
      </div>
    </div>

    <div v-if="['visit_cancel', 'visit_created', 'visit_time_changed'].includes(object.notification_type)">
      <CheckboxGroup class="d-inline-block mt-3"
                     fid="send_only_during_day"
                     v-model="object.send_only_during_day"
                     :label="$t('do_not_send_at_night_time')"/>
    </div>

  </div>
</template>

<script setup>
import CheckboxGroup from "@/components/form/CheckboxGroup"
import InputGroup from "@/components/form/InputGroup"
import {laboratoryOrderStatuses, notificationVisitTypes, timeTypes} from "@/dictionaries/dictionary"
import SelectGroup from "@/components/form/SelectGroup"
import TagsList from "@/components/parts/crm/TagsList.vue"
import { useStore} from "vuex"
import TimeGroup from "@/components/form/TimeGroup.vue"
import {computed, getCurrentInstance, onBeforeMount, onMounted, ref, watch} from "vue"

const props = defineProps({
  object: {
    type: Object
  },
  selectOptions: {},
  field: {},
  required: {},
  value: {},
  selectModels: {}
})

let i18n = null
const store = useStore()

const patientRoleId = 5
const taskChannelId = 8

const rights = computed(() => store.state.auth.rights)
const eligibleForPatientResponse = computed(() => {
  return Number(props.object.recipient_role_id) === patientRoleId
      && ['before_visit', 'visit_time_changed', 'before_visit_day_in_advance'].includes(props.object.notification_type)
})

const confirm_visits_through_message = ref(false)

const parseMinutes = () => {
  let timeType = 'minutes'
  if(props.object.minutes) {
    if(props.object.minutes % (24 * 60) === 0) {
      props.object.minutes = props.object.minutes / (24 * 60)
      timeType = 'days'
    } else if(props.object.minutes % (60) === 0) {
      props.object.minutes = props.object.minutes / (60)
      timeType = 'hours'
    }
  }
  props.object.timeType = timeType
}

const dayOptionsForAfterTheVisit = [
  { id: 0, title: 'same_day'},
  { id: 1440, title: 'after_one_day'},
]

onBeforeMount(() => {
  i18n = (getCurrentInstance())?.ctx?.$i18n
})

onMounted(() => {
  parseMinutes()
  if(props.object.notification_channels) {
    props.object.notification_channels = props.object.notification_channels.map(({id}) => id)
  }
  if(props.object.confirm_response) {
    confirm_visits_through_message.value = true
  }
})

const titleForChannel = (channel) => {
  const title = channel.title
  if(title === 'WebPush') return i18n.t('push_notifications')
  if(title === 'Task') return i18n.t('task')
  return channel.title
}

const notificationChannelsFiltered = computed(() => {
  return props.selectModels.notification_channels.filter(channel => {
    if(Number(props.object.recipient_role_id) === patientRoleId) {
      if(channel.id === taskChannelId) return false
    }
    return true
  })
})

watch(confirm_visits_through_message, (val) => {
  if(!val) {
    props.object.confirm_response = ''
    props.object.cancel_response = ''
  }
})

watch(eligibleForPatientResponse, (val) => {
  if(!val) {
    confirm_visits_through_message.value = false
  }
})

watch(() => props.object.notification_type, (val) => {
  if(val === 'after_visit_day_passed') {
    props.object.cron_offset_minutes = 0
  } else {
    props.object.cron_offset_minutes = null
  }
})

watch(() => props.object.recipient_role_id, () => {
  if(Number(props.object.recipient_role_id) === patientRoleId && props.object.notification_channels.includes(taskChannelId)) {
    props.object.notification_channels = props.object.notification_channels.filter(x => Number(x) !== taskChannelId)
  }
})
</script>
